import quickFetch, { AjaxOptions } from 'quick-fetch';
import { DocumentNode } from 'graphql';
import { stableStringify } from './internal/stableStringify';

// eslint-disable-next-line hubspot-dev/no-declarations
declare const __GRAPHQL_PARSE_PLUGIN__: boolean | undefined;

interface GraphQLRequest {
  operationName: string;
  query: string;
  variables?: Record<string, any>;
}

export type MakeGqlEarlyRequestOptions = AjaxOptions & {
  operation: {
    variables?: Record<string, any>;
    /**
     * @deprecated this field is no longer used
     */
    operationName?: string;
    query: { query: string; id: string; operationName: string };
  };
};

const makeGqlEarlyRequest = ({
  operation,
  ...rest
}: MakeGqlEarlyRequestOptions) => {
  const requestName = stableStringify({
    id: operation.query.id,
    operationName: operation.query.operationName,
    variables: operation.variables || {},
  });

  const data: GraphQLRequest = {
    operationName: operation.query.operationName,
    query: operation.query.query,
  };

  if (operation.variables) {
    data.variables = operation.variables;
  }

  quickFetch.makeEarlyRequest(requestName, {
    ...rest,
    type: 'POST',
    data: JSON.stringify(data),
    dataType: 'json',
    contentType: 'application/json',
  });
};

export interface GetGqlEarlyRequestOptions {
  operationName: string;
  query: DocumentNode;
  variables?: Record<string, any>;
}

const getGqlRequestName = ({
  operationName,
  variables,
  query,
}: GetGqlEarlyRequestOptions) => {
  return stableStringify({
    operationName,
    variables: variables || {},
    id: (query as any).id,
  });
};

const getGqlEarlyRequestState = ({
  operationName,
  variables,
  query,
}: GetGqlEarlyRequestOptions) => {
  // If the graphqlParse plugin is not enabled the `query` will not have an `id` property
  if (
    typeof __GRAPHQL_PARSE_PLUGIN__ === 'undefined' ||
    __GRAPHQL_PARSE_PLUGIN__ === false
  ) {
    return null;
  }

  const requestName = getGqlRequestName({
    operationName,
    variables,
    query,
  });

  return quickFetch.getRequestStateByName(requestName);
};

const getGqlEarlyRequest = ({
  operationName,
  variables,
  query,
}: GetGqlEarlyRequestOptions) => {
  // If the graphqlParse plugin is not enabled the `query` will not have an `id` property
  if (
    typeof __GRAPHQL_PARSE_PLUGIN__ === 'undefined' ||
    __GRAPHQL_PARSE_PLUGIN__ === false
  ) {
    return null;
  }

  const requestName = getGqlRequestName({
    operationName,
    variables,
    query,
  });

  const requestState = quickFetch.getRequestStateByName(requestName);

  if (requestState && !requestState.error) {
    return new Promise((resolve, reject) => {
      requestState.whenFinished((response: any) => {
        quickFetch.removeEarlyRequest(requestName);
        resolve(response);
      });
      requestState.onError((_, errorMessage) => {
        quickFetch.removeEarlyRequest(requestName);
        reject(errorMessage);
      });
    });
  }
  return null;
};

export { getGqlEarlyRequest, makeGqlEarlyRequest, getGqlEarlyRequestState };
